/**
 * SortableTableRow Component
 * Draggable table row for quotation items with expand/collapse details
 * Extracted from QuotationEditorModal.jsx
 */
import React from "react";
import {
  GripVertical,
  Image as ImageIcon,
  ChevronDown,
  Trash2,
  Search,
  CheckSquare,
  Square
} from "lucide-react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import AutocompleteInput from "./AutocompleteInput";

const formatCurrency = (value, currency = "USD") => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatNumber = (value, decimals = 2) => {
  return (value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const SortableTableRow = ({
  item,
  index,
  isSelected,
  isModified,
  isExpanded,
  toggleItemSelection,
  toggleItemExpansion,
  setPreviewImage,
  setPreviewItemCode,
  setPreviewItemId,
  handleItemChange,
  handleItemChangeWithLookup,
  handleDeleteItem,
  handleLookupPrice,
  getItemTotalWithUtiSum,
  autocompleteData
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id || index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f0f9ff' : isModified ? '#fef9c3' : undefined,
  };

  return (
    <>
      <TableRow 
        ref={setNodeRef} 
        style={style} 
        className={`${isDragging ? 'shadow-lg' : ''} ${isModified ? 'border-l-4 border-l-amber-400' : ''} ${isExpanded ? 'bg-blue-50/50' : ''}`}
        data-testid={`item-row-${index}`}
      >
        <TableCell className="p-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
            title="Arrastrar para reordenar"
            data-testid={`drag-handle-${index}`}
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </button>
        </TableCell>
        <TableCell className="p-1">
          <button
            onClick={() => toggleItemSelection(index)}
            className="flex items-center justify-center w-6 h-6 hover:bg-slate-100 rounded"
            data-testid={`select-item-${index}`}
          >
            {isSelected ? (
              <CheckSquare className="w-4 h-4 text-blue-600" />
            ) : (
              <Square className="w-4 h-4 text-slate-400" />
            )}
          </button>
        </TableCell>
        <TableCell className="p-2">
          {item.image_data ? (
            <img
              src={item.image_data}
              alt=""
              className="w-20 h-20 object-contain rounded border cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400 transition-all bg-white"
              onClick={() => {
                setPreviewImage(item.image_data);
                setPreviewItemCode(item.item_code || `Item ${index + 1}`);
                setPreviewItemId(item.id || index);
              }}
              title="Click para ampliar"
              data-testid={`item-image-${index}`}
            />
          ) : (
            <div className="w-20 h-20 bg-slate-100 rounded flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-slate-400" />
            </div>
          )}
        </TableCell>
        <TableCell>
          <Input
            value={item.item_code || ""}
            onChange={(e) => handleItemChange(index, 'item_code', e.target.value)}
            className="h-8 text-sm w-14"
            data-testid={`item-code-${index}`}
          />
        </TableCell>
        <TableCell>
          <AutocompleteInput
            value={item.description || ""}
            onChange={(val) => handleItemChange(index, 'description', val)}
            onSelect={(val) => handleItemChangeWithLookup(index, 'description', val)}
            suggestions={autocompleteData.descriptions}
            className="h-8 text-sm min-w-[100px]"
          />
        </TableCell>
        <TableCell>
          <AutocompleteInput
            value={item.series || ""}
            onChange={(val) => handleItemChange(index, 'series', val)}
            onSelect={(val) => handleItemChangeWithLookup(index, 'series', val)}
            suggestions={autocompleteData.series}
            className="h-8 text-sm w-14"
          />
        </TableCell>
        <TableCell>
          <AutocompleteInput
            value={item.material || ""}
            onChange={(val) => handleItemChange(index, 'material', val)}
            onSelect={(val) => handleItemChangeWithLookup(index, 'material', val)}
            suggestions={autocompleteData.materials}
            className="h-8 text-sm w-14"
          />
        </TableCell>
        <TableCell>
          <AutocompleteInput
            value={item.color || ""}
            onChange={(val) => handleItemChange(index, 'color', val)}
            onSelect={(val) => handleItemChangeWithLookup(index, 'color', val)}
            suggestions={autocompleteData.colors}
            className="h-8 text-sm w-14"
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            step="0.01"
            value={item.width_m || ""}
            onChange={(e) => handleItemChange(index, 'width_m', parseFloat(e.target.value) || 0)}
            className="h-8 text-sm w-20"
            data-testid={`item-width-${index}`}
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            step="0.01"
            value={item.height_m || ""}
            onChange={(e) => handleItemChange(index, 'height_m', parseFloat(e.target.value) || 0)}
            className="h-8 text-sm w-20"
            data-testid={`item-height-${index}`}
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            min="1"
            value={item.quantity || 1}
            onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
            className="h-8 text-sm w-16"
            data-testid={`item-quantity-${index}`}
          />
        </TableCell>
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleItemExpansion(index)}
            className={`h-8 w-8 transition-transform ${isExpanded ? 'rotate-180 bg-blue-100' : ''}`}
            title={isExpanded ? "Contraer detalles" : "Expandir detalles"}
            data-testid={`expand-item-${index}`}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        </TableCell>
        <TableCell>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => handleDeleteItem(index)}
            title="Eliminar item"
            data-testid={`delete-item-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </TableCell>
      </TableRow>
      
      {/* Expandable row with additional details */}
      {isExpanded && (
        <TableRow className="bg-gradient-to-r from-blue-50 to-slate-50 border-l-4 border-l-blue-400">
          <TableCell colSpan={13} className="p-3">
            <div className="flex items-center justify-between gap-6">
              {/* Calculated area */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">Área:</span>
                <span className="font-mono text-sm font-semibold bg-white px-3 py-1 rounded border">
                  {formatNumber(item.area_m2, 4)} m²
                </span>
              </div>
              
              {/* Price per m² with lookup button */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">$/m²:</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    step="0.01"
                    value={item.cost_per_m2 || ""}
                    onChange={(e) => handleItemChange(index, 'cost_per_m2', parseFloat(e.target.value) || 0)}
                    className="h-8 text-sm w-24"
                    title={item.matched_type_name ? `Precio de: ${item.matched_type_name}` : ''}
                    data-testid={`item-cost-${index}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={() => handleLookupPrice(index)}
                    title="Buscar precio en Lista de Precio Venta"
                    data-testid={`lookup-price-${index}`}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Buscar
                  </Button>
                </div>
              </div>
              
              {/* Subtotal */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">Subtotal:</span>
                <span className="font-mono text-base font-bold text-green-700 bg-green-50 px-4 py-1.5 rounded border border-green-200">
                  {formatCurrency(getItemTotalWithUtiSum(item))}
                </span>
              </div>
              
              {/* Observations */}
              <div className="flex-1 flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">Notas:</span>
                <Input
                  value={item.observations || ""}
                  onChange={(e) => handleItemChange(index, 'observations', e.target.value)}
                  placeholder="Observaciones del item..."
                  className="h-8 text-sm flex-1"
                  data-testid={`item-observations-${index}`}
                />
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default SortableTableRow;
